/**
 * Timeline transformation utilities for task visualization
 * Pattern: Mirrors timeline-utils.js phase transformation logic
 */

import { parseIntervalToDays } from "~/common/utils/date-utils"
import { WEEK_MS } from "../crop-timeline/timeline-constants"

/**
 * Transform task data to week-based format for timeline rendering
 * Similar to scheduleDataToWeekFormat but for tasks
 *
 * @param {Array} taskData - Array of { crop, tasks: [...] }
 * @param {boolean} dayPrecision - If true, use day precision (for interactive mode)
 * @param {Date} timelineStartDate - The timeline's START_DATE (dynamic based on frost dates)
 * @param {number} maxWeeks - Maximum number of weeks in timeline
 * @returns {Array} Crops with tasks in week format
 */
export function taskDataToWeekFormat(taskData, dayPrecision = false, timelineStartDate = null, maxWeeks = 52) {
  if (!taskData || taskData.length === 0) {
    return []
  }

  if (!timelineStartDate) {
    console.warn('No timeline start date provided to taskDataToWeekFormat')
    return []
  }

  return taskData
    .filter(item => item.tasks && item.tasks.length > 0)
    .map(item => {
      const tasks = item.tasks.map(task => {
        const taskStart = new Date(task.task_window_start)
        const durationDays = parseIntervalToDays(task.duration)

        if (dayPrecision) {
          // Day precision: calculate exact day position and length
          const daysFromStart = (taskStart - timelineStartDate) / (24 * 60 * 60 * 1000)
          const startInWeeks = daysFromStart / 7
          const lenInWeeks = durationDays / 7

          // Day-precision completion offset (used for badge positioning regardless of mode)
          const completed_at_weeks = (task.state === 'completed' && task.completed_at)
            ? (new Date(task.completed_at) - timelineStartDate) / (WEEK_MS)
            : null

          return {
            // Preserve original task data
            ...task,
            // Append usefull data transformations
            type: task.task_code,
            start: Math.max(0, Math.min(maxWeeks, startInWeeks)), // Allow fractional weeks
            len: Math.max(1 / 7, lenInWeeks), // Minimum 1 day
            label: task.task_type?.name || task.task_code,
            completed_at_weeks,
            readOnly: task.readOnly ?? false,
            single_completion: task.task_type?.single_completion ?? true,
            submissions: task.submissions
              ? task.submissions.map(sub => ({
                  ...sub,
                  submitted_at_weeks: (new Date(sub.submitted_at) - timelineStartDate) / (WEEK_MS),
                }))
              : null,
          }
        } else {
          // Week precision (original behavior for dashboard)
          const weekFromStart = Math.floor((taskStart - timelineStartDate) / (WEEK_MS))

          // Day-precision completion offset (used for badge positioning regardless of mode)
          const completed_at_weeks = (task.state === 'completed' && task.completed_at)
            ? (new Date(task.completed_at) - timelineStartDate) / (WEEK_MS)
            : null

          return {
            type: task.task_code,
            start: Math.max(0, Math.min(maxWeeks - 1, weekFromStart)), // Clamp to timeline range
            len: Math.max(1, Math.ceil(durationDays / 7)),
            label: task.task_type?.name || task.task_code,
            // Preserve original task data
            task_code: task.task_code,
            task_window_start: task.task_window_start,
            duration: task.duration,
            notes: task.notes,
            state: task.state,
            completed_at: task.completed_at ?? null,
            completed_at_weeks,
            id: task.id,
            readOnly: task.readOnly ?? false,
            single_completion: task.task_type?.single_completion ?? true,
            submissions: task.submissions
              ? task.submissions.map(sub => ({
                  ...sub,
                  submitted_at_weeks: (new Date(sub.submitted_at) - timelineStartDate) / (WEEK_MS),
                }))
              : null,
          }
        }
      })

      return {
        ...item.crop,
        tasks
      }
    })
}
